import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { SHARED_SESSION_NOTIFICATION_TYPES } from "@/constants/notifications";
import { chatService } from "@/lib/services";
import type { SharedSessionSummary } from "@/types";
import { useAuth } from "./AuthContext";
import { useNotificationEvent } from "./NotificationContext";

interface SharedSessionsContextType {
  sharedSessions: SharedSessionSummary[];
  isLoading: boolean;
  refreshSharedSessions: () => Promise<void>;
}

const SharedSessionsContext = createContext<SharedSessionsContextType | null>(null);

/** Minimum time (ms) between API fetches to avoid redundant calls. */
const CACHE_TTL_MS = 30_000; // 30 seconds

export function SharedSessionsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated } = useAuth();

  const [sharedSessions, setSharedSessions] = useState<SharedSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Track the last successful fetch timestamp for cache staleness checks
  const lastFetchedAt = useRef<number>(0);
  // Dedup in-flight requests so concurrent callers share a single promise
  const inflightPromise = useRef<Promise<void> | null>(null);

  const fetchSharedSessions = useCallback(async () => {
    if (!isAuthenticated) return;

    // If data was fetched recently, skip
    if (Date.now() - lastFetchedAt.current < CACHE_TTL_MS) return;

    // If a fetch is already in flight, reuse it instead of firing another
    if (inflightPromise.current) {
      await inflightPromise.current;
      return;
    }

    const promise = (async () => {
      try {
        setIsLoading(true);
        const res = await chatService.getSharedWithMe();
        setSharedSessions(res.data);
        lastFetchedAt.current = Date.now();
      } catch (err) {
        console.error("Failed to load shared sessions:", err);
      } finally {
        setIsLoading(false);
        inflightPromise.current = null;
      }
    })();

    inflightPromise.current = promise;
    await promise;
  }, [isAuthenticated]);

  /** Force-refresh regardless of cache TTL (e.g. after sharing/unsharing). */
  const refreshSharedSessions = useCallback(async () => {
    lastFetchedAt.current = 0; // invalidate cache
    await fetchSharedSessions();
  }, [fetchSharedSessions]);

  // Fetch on mount / when auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchSharedSessions();
    } else {
      setSharedSessions([]);
      lastFetchedAt.current = 0;
    }
  }, [isAuthenticated, fetchSharedSessions]);

  // Refresh immediately when a chat or brainstorm is shared with this user,
  // bypassing the cache.
  useNotificationEvent("notification", ({ type }) => {
    if (SHARED_SESSION_NOTIFICATION_TYPES.has(type)) {
      refreshSharedSessions();
    }
  });

  const value = useMemo<SharedSessionsContextType>(
    () => ({
      sharedSessions,
      isLoading,
      refreshSharedSessions,
    }),
    [sharedSessions, isLoading, refreshSharedSessions],
  );

  return <SharedSessionsContext.Provider value={value}>{children}</SharedSessionsContext.Provider>;
}

export function useSharedSessions(): SharedSessionsContextType {
  const context = useContext(SharedSessionsContext);
  if (!context) {
    throw new Error("useSharedSessions must be used within a SharedSessionsProvider");
  }
  return context;
}
